/**
 * F09 v2 — `/verifications/requests/:id` admin request detail.
 *
 * Panels: documents (E7 proxy viewer), signals by entity type + risk flags,
 * F43 report history, prior requests, duplicate flags (`sameSubject` shown as
 * informational), name-change diff (current vs requested), events timeline,
 * and document-access count + access log.
 *
 * Decisions (Approve / Reject / Request more info) need `verifications:review`;
 * read-only viewers (support) see the request but every document is
 * "Restricted" and no decision button renders.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormat, useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Card, EmptyState, Modal, Spinner, useToast } from '@bravemove/ui-web';
import type { AdminVerificationRequestDetail, VerificationAccessLogRow } from '@bravemove/types';
import { adminVerificationApi } from '../../../api/verification';
import { PageHeader } from '../../../components/PageHeader';
import { useVerificationPermissions } from '../../../hooks/useVerificationPermissions';
import { ApproveDialog, DuplicateMatchList } from '../../../components/verification/ApproveDialog';
import { MoreInfoDialog, RejectDialog } from '../../../components/verification/DecisionDialogs';
import {
  EventsTimeline,
  NameChangeDiff,
  Panel,
  RequestHistoryTable,
  SignalsPanel,
} from '../../../components/verification/DetailPanels';
import { DocumentList } from '../../../components/verification/DocumentList';
import {
  docKindLabel,
  entityPath,
  idTypeLabel,
  kindLabel,
  prerequisiteLabel,
  rejectionLabel,
} from '../../../components/verification/labels';
import {
  EntityTypeBadge,
  RequestStatusBadge,
  StrengthBadge,
  VerificationErrorBanner,
} from '../../../components/verification/VerificationBits';
import { verificationErrorCode } from '../../../components/verification/verificationErrors';

type DialogKind = 'approve' | 'reject' | 'moreInfo' | 'accessLog' | null;

interface LoadState {
  readonly key: string;
  readonly detail: AdminVerificationRequestDetail | null;
  readonly error: unknown;
}

function MetaRow({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-3 text-[13px]">
      <dt className="min-w-36 text-ink-3">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function AccessLogModal({
  requestId,
  onClose,
}: {
  readonly requestId: string;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const { formatDateTime } = useFormat();
  const [rows, setRows] = useState<readonly VerificationAccessLogRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    adminVerificationApi.accessLog(requestId).then(
      (r) => {
        if (!cancelled) setRows(r);
      },
      (e: unknown) => {
        if (!cancelled) setError(e);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  return (
    <Modal open onClose={onClose} title={t('admin:verification.panels.accessLogTitle')} size="lg">
      <div data-testid="access-log">
        {error ? (
          <VerificationErrorBanner error={error} testId="access-log-error" />
        ) : rows === null ? (
          <div className="flex justify-center py-6">
            <Spinner size={24} />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-[13px] text-ink-3">{t('admin:verification.panels.accessLogEmpty')}</p>
        ) : (
          <ul className="space-y-1.5 text-[13px]">
            {rows.map((r) => (
              <li key={r.id} className="rounded-md border border-hairline px-3 py-2">
                {t('admin:verification.panels.accessLogRow', {
                  who: r.accessedByDisplayName ?? r.accessedByAdminId ?? '—',
                  mode: t(`admin:verification.panels.renderMode.${r.renderMode}`),
                  time: formatDateTime(new Date(r.createdAt)),
                })}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

export function VerificationRequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { formatDateTime, formatDate } = useFormat();
  const navigate = useNavigate();
  const toast = useToast();
  const { canReview } = useVerificationPermissions();

  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>({ key: '', detail: null, error: null });
  const [extraViews, setExtraViews] = useState(0);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const key = `${id}#${reloadToken}`;

  useEffect(() => {
    let cancelled = false;
    const k = `${id}#${reloadToken}`;
    adminVerificationApi.detail(id).then(
      (detail) => {
        if (cancelled) return;
        setExtraViews(0);
        setState({ key: k, detail, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setState({ key: k, detail: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [id, reloadToken]);

  const loading = state.key !== key;
  const reload = () => setReloadToken((n) => n + 1);
  const fmt = (iso: string | null) => (iso ? formatDateTime(new Date(iso)) : '—');

  const header = (
    <PageHeader
      title={t('admin:verification.pageTitle.request')}
      actions={
        <Button size="sm" variant="ghost" icon="back" onClick={() => navigate('/verifications')}>
          {t('admin:verification.common.back')}
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {header}
        <div className="flex justify-center py-16" data-testid="request-loading">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  const d = state.detail;
  if (!d) {
    return (
      <div className="space-y-5">
        {header}
        {verificationErrorCode(state.error) === 'VERIFICATION_NOT_FOUND' ? (
          <EmptyState title={t('admin:verification.detail.notFound')} />
        ) : (
          <div className="space-y-2">
            <VerificationErrorBanner error={state.error} testId="request-error" />
            <Button size="sm" variant="secondary" onClick={reload}>
              {t('admin:verification.common.retry')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Plan §7 transitions: approve + requestMoreInfo are `pending →` only;
  // reject is `pending | more_info_needed →`. A request waiting on the
  // requester's reply can be rejected but not approved.
  const isPending = d.status === 'pending';
  const isOpen = isPending || d.status === 'more_info_needed';
  const canDecide = canReview && isOpen;
  const accessCount = d.accessCount + extraViews;
  const docDuplicates = d.documents.flatMap((doc) => doc.duplicateMatches);
  // Two documents of this request can match the SAME foreign document (e.g. an
  // identical file re-uploaded in a more-info round) — list each match once
  // (the list key is `matchKind:requestId:docId`).
  const allDuplicates = [...d.identityDuplicates, ...docDuplicates].filter(
    (m, i, all) =>
      all.findIndex(
        (o) => o.matchKind === m.matchKind && o.requestId === m.requestId && o.docId === m.docId,
      ) === i,
  );

  const afterDecision = (toastKey: string) => {
    setDialog(null);
    toast.push({ tone: 'success', title: t(toastKey) });
    reload();
  };

  return (
    <div className="space-y-5" data-testid="verification-request-detail">
      {header}

      {!canReview ? (
        <Banner tone="info">{t('admin:verification.detail.readOnlyNotice')}</Banner>
      ) : null}

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[20px] font-extrabold text-ink" data-testid="request-entity-name">
                {d.entityName}
              </h2>
              <EntityTypeBadge type={d.entityType} />
              <RequestStatusBadge status={d.status} />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[13px] text-ink-2">
              <span>{kindLabel(t, d.requestKind)}</span>
              <StrengthBadge score={d.evaluatorScore} riskFlags={d.riskFlags} />
              <Button
                size="xs"
                variant="link"
                onClick={() => navigate(entityPath(d.entityType, d.entityId))}
              >
                {t('admin:verification.common.openEntity')}
              </Button>
            </div>
          </div>
          {canDecide ? (
            <div className="flex flex-wrap gap-2" data-testid="request-decision-actions">
              {isPending ? (
                <Button
                  variant="primary"
                  onClick={() => setDialog('approve')}
                  data-testid="request-approve"
                >
                  {t('admin:verification.detail.approve')}
                </Button>
              ) : null}
              {isPending ? (
                <Button
                  variant="secondary"
                  onClick={() => setDialog('moreInfo')}
                  data-testid="request-more-info"
                >
                  {t('admin:verification.detail.moreInfo')}
                </Button>
              ) : null}
              <Button
                variant="destructive"
                onClick={() => setDialog('reject')}
                data-testid="request-reject"
              >
                {t('admin:verification.detail.reject')}
              </Button>
            </div>
          ) : null}
        </div>
        <dl className="mt-4 space-y-1">
          <MetaRow label={t('admin:verification.detail.subject')}>
            {d.identitySubjectNameSnapshot ?? d.subjectDisplayName ?? '—'}
          </MetaRow>
          <MetaRow label={t('admin:verification.detail.requester')}>
            {d.requesterDisplayName ?? '—'}
          </MetaRow>
          <MetaRow label={t('admin:verification.detail.submitted')}>{fmt(d.submittedAt)}</MetaRow>
          <MetaRow label={t('admin:verification.detail.source')}>
            {t(`admin:verification.source.${d.source}`)}
          </MetaRow>
          {d.requesterNote ? (
            <MetaRow label={t('admin:verification.detail.requesterNote')}>
              {d.requesterNote}
            </MetaRow>
          ) : null}
          {d.idDocumentRecord ? (
            <MetaRow label={t('admin:verification.detail.decision')}>
              {t('admin:verification.detail.idRecord', {
                type: idTypeLabel(t, d.idDocumentRecord.docType),
                country: d.idDocumentRecord.issuingCountry,
              })}
            </MetaRow>
          ) : null}
          {d.decisionReasonCategory ? (
            <MetaRow label={t('admin:verification.detail.decision')}>
              {rejectionLabel(t, d.decisionReasonCategory)}
              {d.decisionNote ? ` — ${d.decisionNote}` : ''}
            </MetaRow>
          ) : null}
          {d.adminInternalNote ? (
            <MetaRow label={t('admin:verification.detail.internalNote')}>
              {d.adminInternalNote}
            </MetaRow>
          ) : null}
        </dl>
      </Card>

      {d.moreInfoPrompt ? (
        <Banner tone="warning" title={t('admin:verification.detail.moreInfoPrompt')}>
          <p className="whitespace-pre-wrap">{d.moreInfoPrompt}</p>
          {d.moreInfoRequestedDocumentKinds.length > 0 ? (
            <p className="mt-1">
              {d.moreInfoRequestedDocumentKinds.map((k) => docKindLabel(t, k)).join(', ')}
            </p>
          ) : null}
          {d.moreInfoDueAt ? (
            <p className="mt-1">
              {t('admin:verification.detail.moreInfoDue')}: {formatDate(new Date(d.moreInfoDueAt))}
            </p>
          ) : null}
          {d.responseNote ? (
            <p className="mt-1">
              {t('admin:verification.detail.responseNote')}: {d.responseNote}
            </p>
          ) : null}
        </Banner>
      ) : null}

      {d.unmetPrerequisites.length > 0 ? (
        <div data-testid="request-unmet-prerequisites">
          <Banner tone="danger" title={t('admin:verification.detail.unmetPrerequisites')}>
            <ul className="list-disc ps-5">
              {d.unmetPrerequisites.map((c) => (
                <li key={c}>{prerequisiteLabel(t, c)}</li>
              ))}
            </ul>
          </Banner>
        </div>
      ) : null}

      {d.missingDocumentGroups.length > 0 ? (
        <Banner tone="warning" title={t('admin:verification.detail.missingDocuments')}>
          <ul className="list-disc ps-5">
            {d.missingDocumentGroups.map((g) => (
              <li key={g.labelKey}>{t(g.labelKey)}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-5">
          <Panel title={t('admin:verification.documents.title')} testId="panel-documents">
            <DocumentList
              documents={d.documents}
              canReview={canReview}
              onViewed={() => setExtraViews((n) => n + 1)}
            />
          </Panel>

          {d.requestKind === 'name_change' ? (
            <NameChangeDiff
              currentName={d.currentEntityName}
              requestedName={d.requestedName}
              currentLegalName={d.currentEntityLegalName}
              requestedLegalName={d.requestedLegalName}
            />
          ) : null}

          <Panel title={t('admin:verification.panels.duplicates')} testId="panel-duplicates">
            {allDuplicates.length === 0 ? (
              <p className="text-[13px] text-ink-3">
                {t('admin:verification.panels.noDuplicates')}
              </p>
            ) : (
              <DuplicateMatchList matches={allDuplicates} testId="request-duplicate-matches" />
            )}
          </Panel>

          <Panel title={t('admin:verification.panels.events')} testId="panel-events">
            <EventsTimeline events={d.events} />
          </Panel>
        </div>

        <div className="space-y-5">
          <SignalsPanel signals={d.evaluatorSignals} riskFlags={d.riskFlags} />

          <Panel title={t('admin:verification.panels.reports')} testId="panel-reports">
            <dl className="space-y-1">
              <MetaRow label={t('admin:verification.panels.openReports')}>
                {d.reportHistory.openCount}
              </MetaRow>
              <MetaRow label={t('admin:verification.panels.actionedReports')}>
                {d.reportHistory.actionedCount365d}
              </MetaRow>
            </dl>
          </Panel>

          <Panel title={t('admin:verification.panels.priorRequests')} testId="panel-prior">
            {d.priorRequests.length === 0 ? (
              <p className="text-[13px] text-ink-3">
                {t('admin:verification.panels.noPriorRequests')}
              </p>
            ) : (
              <RequestHistoryTable rows={d.priorRequests} currentId={d.id} />
            )}
          </Panel>

          <Panel
            title={t('admin:verification.panels.access')}
            testId="panel-access"
            action={
              canReview ? (
                <Button
                  size="xs"
                  variant="link"
                  data-testid="request-access-log-link"
                  onClick={() => setDialog('accessLog')}
                >
                  {t('admin:verification.panels.viewAccessLog')}
                </Button>
              ) : null
            }
          >
            <p className="text-[13px] text-ink-2" data-testid="request-access-count">
              {t('admin:verification.panels.accessCount', { count: accessCount })}
            </p>
          </Panel>
        </div>
      </div>

      {dialog === 'approve' ? (
        <ApproveDialog
          detail={d}
          onClose={() => setDialog(null)}
          onApproved={() => afterDecision('admin:verification.toast.approved')}
        />
      ) : null}
      {dialog === 'reject' ? (
        <RejectDialog
          requestId={d.id}
          onClose={() => setDialog(null)}
          onDone={() => afterDecision('admin:verification.toast.rejected')}
        />
      ) : null}
      {dialog === 'moreInfo' ? (
        <MoreInfoDialog
          requestId={d.id}
          entityType={d.entityType}
          onClose={() => setDialog(null)}
          onDone={() => afterDecision('admin:verification.toast.moreInfo')}
        />
      ) : null}
      {dialog === 'accessLog' ? (
        <AccessLogModal requestId={d.id} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
